import {
  addDays,
  areRangesOverlapping,
  format,
  isAfter,
  isSameDay,
  setHours,
  setMinutes,
} from "date-fns";
import { Maybe } from "true-myth";
import {
  equals,
  filter,
  find,
  findIndex,
  flatten,
  has,
  map,
  not,
  omit,
  pipe,
  pluck,
  prop,
  propEq,
  reject,
  uniqBy,
  update,
} from "ramda";

import { INewShift } from "actions/scheduling/blocks";
import {
  Block,
  IPerson,
  IShift,
  ISchedule,
  IPosUser,
  IUnavailabilityBlock,
  Frequency,
  IPeople,
} from "types/schedules";
import { dateToWeekday } from "helpers/dates";
import { findByUuid, replacePersonInSchedule } from "helpers/transforms/people";
import {
  isUnassignedShiftGroup,
  mergePeopleIntoSchedule,
} from "helpers/transforms/schedule";
import { ExistingBlock } from "helpers/schedule/time_block_modal";
import { trackEvent } from "helpers/analytics";

function composeBlockTime(hour: number, minute: number, date: Date) {
  const dateWithHours: Date = setHours(date, hour);
  return setMinutes(dateWithHours, minute);
}

function formatNewShift(newShift: INewShift): IShift {
  const times = formatTimeBlock(omit(["employee", "role"], newShift));
  return {
    ...newShift,
    ...times,
  };
}

export interface ITimeBlock {
  startHour: number;
  endHour: number;
  startMinute: number;
  endMinute: number;
  date: Date;
}

interface IDateTimeBlock {
  startTime: Date;
  endTime: Date;
}

enum TimeConflictMessages {
  EX_UNAVAIL = "Employee is already unavailable at this time.\nSelect another time or edit original unavailability.",
  EX_SHIFT = "Employee already has a shift during this time.\nSelect another time or edit existing shift.",
  EX_UNAVAIL_OVERRIDE = "Employee is unavailable at this time.\nAdd shift anyway?",
  NONE = "",
}

interface IConflictInfo {
  conflict: string;
  canOverride: boolean;
}

function findPersonTimeConflict(
  { assignee, shifts, unavail }: IPerson,
  newBlock: IDateTimeBlock,
  existingBlock: ExistingBlock,
  isUnavail: boolean
): IConflictInfo {
  let conflict: string = TimeConflictMessages.NONE;
  let canOverride = false;

  // Check if they are already unavailable at this time
  const hasUnavail = unavail
    .map(allUnavail =>
      allUnavail.some(
        thisUnavail =>
          !existingBlock.equals(Maybe.of(thisUnavail)) &&
          areRangesOverlapping(
            newBlock.startTime,
            newBlock.endTime,
            thisUnavail.startTime,
            thisUnavail.endTime
          )
      )
    )
    .unwrapOr(false);

  if (hasUnavail) {
    conflict = isUnavail
      ? TimeConflictMessages.EX_UNAVAIL
      : TimeConflictMessages.EX_UNAVAIL_OVERRIDE;
  } else {
    // Check for a shift at this time
    const hasShift = shifts.some(
      shift =>
        !existingBlock.equals(Maybe.of(shift)) &&
        areRangesOverlapping(
          newBlock.startTime,
          newBlock.endTime,
          shift.startTime,
          shift.endTime
        )
    );

    if (hasShift) {
      conflict = TimeConflictMessages.EX_SHIFT;
    }
  }

  if (conflict === TimeConflictMessages.NONE) {
    return { conflict, canOverride: false };
  } else {
    canOverride = conflict === TimeConflictMessages.EX_UNAVAIL_OVERRIDE;
    conflict = assignee.match({
      Just: thisAssignee =>
        conflict.replace("Employee", thisAssignee.firstName),
      Nothing: () => conflict,
    });
    return { conflict, canOverride };
  }
}

function formatTimeBlock(timeBlock: ITimeBlock): IDateTimeBlock {
  const startTime: Date = composeBlockTime(
    timeBlock.startHour,
    timeBlock.startMinute,
    timeBlock.date
  );

  let endTime: Date = composeBlockTime(
    timeBlock.endHour,
    timeBlock.endMinute,
    timeBlock.date
  );

  if (!isAfter(endTime, startTime)) {
    endTime = addDays(endTime, 1);
  }

  return { startTime, endTime };
}

function isBlockOnDate(date: Date, timeBlock: Block) {
  return isSameDay(timeBlock.startTime, date);
}

function isBlockUnavail(block: Block): boolean {
  return has("frequency", block);
}

function filterBlocksByDate(date: Date, blocks: Block[]): Block[] {
  return filter(block => {
    if (isBlockOnDate(date, block)) {
      return true;
    }
    if (isBlockUnavail(block)) {
      const unavail = block as IUnavailabilityBlock;
      return (
        unavail.frequency === Frequency.WEEKLY &&
        dateToWeekday(date) === unavail.dayOfWeek
      );
    }
    return false;
  }, blocks);
}

function convertToPersonWithShifts(newShift: INewShift): IPerson {
  return {
    assignee: newShift.employee,
    shifts: [formatNewShift(newShift)],
    unavail: Maybe.nothing(),
  };
}

function getAssigneeId(assignee: Maybe<IPosUser>): string {
  return assignee.match({
    Just: posUser => posUser.uuid,
    Nothing: () => "",
  });
}

function getPeopleShifts(people: IPeople): IShift[] {
  return pipe<IPeople, IShift[][], IShift[]>(
    pluck("shifts"),
    flatten as (shifts: IShift[][]) => IShift[]
  )(people);
}

function updateShiftWithNewOwner(
  newShift: INewShift,
  oldShift: IShift,
  oldShiftPerson: IPerson,
  schedule: ISchedule
): ISchedule {
  const newShiftOwner = convertToPersonWithShifts(newShift);
  const scheduleWithoutOldShift = deleteShiftFromSchedule(
    oldShift,
    oldShiftPerson.assignee,
    schedule
  );

  return mergePeopleIntoSchedule([newShiftOwner], scheduleWithoutOldShift);
}

function updateShiftWithSameOwner(
  shiftIndex: number,
  newShift: INewShift,
  oldShiftPerson: IPerson,
  schedule: ISchedule
): ISchedule {
  const updatedShifts = update(
    shiftIndex,
    formatNewShift(newShift),
    oldShiftPerson.shifts
  );

  const personWithUpdatedShift = {
    ...oldShiftPerson,
    shifts: updatedShifts,
  };

  return replacePersonInSchedule(
    schedule,
    personWithUpdatedShift,
    oldShiftPerson
  );
}

function updateEmployeesShift(
  newShift: INewShift,
  oldShift: IShift,
  oldShiftPerson: IPerson,
  schedule: ISchedule
): ISchedule {
  const shiftIndex = findIndex(
    propEq("uuid", oldShift.uuid),
    oldShiftPerson.shifts
  );
  if (shiftIndex > -1) {
    const newShiftAssigneeId: string = getAssigneeId(newShift.employee);
    const oldShiftAssigneeId: string = getAssigneeId(oldShiftPerson.assignee);

    if (equals(newShiftAssigneeId, oldShiftAssigneeId)) {
      return updateShiftWithSameOwner(
        shiftIndex,
        newShift,
        oldShiftPerson,
        schedule
      );
    } else {
      return updateShiftWithNewOwner(
        newShift,
        oldShift,
        oldShiftPerson,
        schedule
      );
    }
  }

  return schedule;
}

function replaceShift(
  foundPerson: Maybe<IPerson>,
  schedule: ISchedule,
  shift: INewShift,
  oldShift: IShift
): ISchedule {
  return foundPerson.match({
    Just: group => updateEmployeesShift(shift, oldShift, group, schedule),
    Nothing: () => schedule,
  });
}

function addOrUpdateShift(
  shift: INewShift,
  existingShift: Maybe<IShift>,
  existingAssignee: Maybe<IPosUser>,
  schedule: ISchedule
): ISchedule {
  const foundPerson: Maybe<IPerson> = existingAssignee.match({
    Just: posUser => findByUuid(posUser.uuid, schedule.people),
    Nothing: () => Maybe.of(find(isUnassignedShiftGroup, schedule.people)),
  });

  return existingShift.match({
    Just: oldShift => replaceShift(foundPerson, schedule, shift, oldShift),
    Nothing: () =>
      mergePeopleIntoSchedule([convertToPersonWithShifts(shift)], schedule),
  });
}

function deleteShiftFromSchedule(
  shift: IShift,
  assignee: Maybe<IPosUser>,
  schedule: ISchedule
): ISchedule {
  const foundPerson: Maybe<IPerson> = assignee.match({
    Just: posUser => findByUuid(posUser.uuid, schedule.people),
    Nothing: () => Maybe.of(find(isUnassignedShiftGroup, schedule.people)),
  });

  return foundPerson.match({
    Just: foundEmployee => {
      const employeeWithShiftDropped = {
        ...foundEmployee,
        shifts: reject(propEq("uuid", shift.uuid), foundEmployee.shifts),
      };

      return replacePersonInSchedule(
        schedule,
        employeeWithShiftDropped,
        foundEmployee
      );
    },
    Nothing: () => schedule,
  });
}

function dedupeShifts(schedule: ISchedule) {
  const dedupedPeople = map(person => {
    const dedupedShifts = uniqBy<IShift, string>(prop("uuid"), person.shifts);
    return {
      ...person,
      shifts: dedupedShifts,
    };
  }, schedule.people);
  return {
    ...schedule,
    people: dedupedPeople,
  };
}

function sameShiftHour(oldShiftTime: Date, newShiftHour: number): boolean {
  return parseInt(format(oldShiftTime, "H"), 10) === newShiftHour;
}

function sameShiftMinute(oldShiftTime: Date, newShiftMinute: number): boolean {
  return parseInt(format(oldShiftTime, "mm"), 10) === newShiftMinute;
}

function trackEditAssignedShiftEvents(
  newShift: INewShift,
  lastAssignee: IPosUser,
  oldShift: IShift
) {
  newShift.employee.match({
    Just: nextAssignee => {
      if (not(equals(nextAssignee.uuid, lastAssignee.uuid))) {
        trackEvent("edit the employee of an assigned shift");
      }
    },
    Nothing: () => {
      trackEvent("switch an assigned shift to unassigned");
    },
  });

  if (not(equals(oldShift.role.uuid, newShift.role.uuid))) {
    trackEvent("edit the role of an assigned shift");
  }

  if (not(isSameDay(oldShift.startTime, newShift.date))) {
    trackEvent("edit the date of an assigned shift");
  }

  if (
    not(
      sameShiftHour(oldShift.startTime, newShift.startHour) &&
        sameShiftMinute(oldShift.startTime, newShift.startMinute)
    )
  ) {
    trackEvent("edits the start time of an assigned shift");
  }

  if (
    not(
      sameShiftHour(oldShift.endTime, newShift.endHour) &&
        sameShiftMinute(oldShift.endTime, newShift.endMinute)
    )
  ) {
    trackEvent("edits the end time of an assigned shift");
  }
}

function trackEditUnassignedShiftEvents(
  newShift: INewShift,
  oldShift: IShift
): void {
  if (newShift.employee.isJust()) {
    trackEvent("assign an employee to an unassigned shift");
  }
  if (not(equals(oldShift.role.uuid, newShift.role.uuid))) {
    trackEvent("edit the role of an unassigned shift");
  }
  if (not(isSameDay(oldShift.startTime, newShift.date))) {
    trackEvent("edit the date of an unassigned shift");
  }
  if (
    not(
      sameShiftHour(oldShift.startTime, newShift.startHour) &&
        sameShiftMinute(oldShift.startTime, newShift.startMinute)
    )
  ) {
    trackEvent("edits the start time of an unassigned shift");
  }
  if (
    not(
      sameShiftHour(oldShift.endTime, newShift.endHour) &&
        sameShiftMinute(oldShift.endTime, newShift.endMinute)
    )
  ) {
    trackEvent("edits the end time of an unassigned shift");
  }
}

function trackEditShiftEvents(
  shift: INewShift,
  oldShift: IShift,
  existingAssignee: Maybe<IPosUser>
): void {
  existingAssignee.match({
    Just: lastAssignee =>
      trackEditAssignedShiftEvents(shift, lastAssignee, oldShift),
    Nothing: () => trackEditUnassignedShiftEvents(shift, oldShift),
  });
}

function trackSaveShiftEvents(
  shift: INewShift,
  existingShift: Maybe<IShift>,
  existingAssignee: Maybe<IPosUser>
): void {
  existingShift.match({
    Just: oldShift => trackEditShiftEvents(shift, oldShift, existingAssignee),
    Nothing: () =>
      shift.employee.match({
        Just: () => trackEvent("create assigned shift"),
        Nothing: () => trackEvent("create unassigned shift"),
      }),
  });
}

export {
  addOrUpdateShift,
  convertToPersonWithShifts,
  dedupeShifts,
  deleteShiftFromSchedule,
  filterBlocksByDate,
  findPersonTimeConflict,
  formatNewShift,
  formatTimeBlock,
  getAssigneeId,
  getPeopleShifts,
  isBlockOnDate,
  isBlockUnavail,
  TimeConflictMessages,
  trackSaveShiftEvents,
};
